import { GetStaticProps, InferGetStaticPropsType, GetStaticPaths } from 'next';
import { useCanonical } from '@hooks';
import { PostListPage } from '@components';
import {
  getAllCategoryNamesAndSlugs2,
  getHnaviOptions,
  getLatestPosts,
  getMostPopularPosts,
  getPosts,
  getUser,
  getUserPostCount,
} from '@api';
import { PER_PAGE_REST } from '@utils';
import { isrRevalidateSec } from '@constants';
import { getHnaviContentCategoriesFromCategories } from '@helpers';
import { authorPath } from '@paths';

const Page: React.FC<InferGetStaticPropsType<typeof getStaticProps>> = (
  props
) => {
  const canonical = useCanonical();

  return (
    <PostListPage
      {...props}
      pageTitle={`${props.author?.name}の記事`}
      seoData={{ canonical }}
      pageCount={Math.ceil(
        (props.posts.pageInfo?.offsetPagination?.total ?? 0) / PER_PAGE_REST
      )}
      paginationBasePath={authorPath(props.authorId).replace(/\/$/, '')}
    />
  );
};

export default Page;

export const getStaticProps = async ({
  params,
}: Parameters<GetStaticProps<any, { pageNo: string; id: string }>>[0]) => {
  const pageNo = Number(params?.pageNo) || 1;
  const authorId = Number(params?.id);
  console.info(`getStaticProps /author/${params?.id}/page/${pageNo}`);

  const author = await getUser({ id: String(authorId) });
  const posts = await getPosts({
    where: {
      offsetPagination: {
        size: PER_PAGE_REST,
        offset: PER_PAGE_REST * (pageNo - 1),
      },
      author: authorId,
    },
  });

  if (!author || !posts.posts?.length) {
    return { notFound: true } as never;
  }

  const categories = await getAllCategoryNamesAndSlugs2();

  return {
    props: {
      posts,
      author,
      categories,
      hnaviContentCategories:
        getHnaviContentCategoriesFromCategories(categories),
      latestPosts: await getLatestPosts(),
      popularPosts: await getMostPopularPosts(),
      hnaviOptions: await getHnaviOptions(),
      pageNo,
      authorId,
    },
    revalidate: isrRevalidateSec,
  };
};

export const getStaticPaths: GetStaticPaths<{
  pageNo: string;
  id: string;
}> = async () => {
  return {
    paths: (await getUserPostCount()).flatMap(({ authorId, postCount }) =>
      new Array(Math.ceil(postCount / PER_PAGE_REST)).fill(0).map((_, i) => ({
        params: { id: String(authorId), pageNo: String(i + 1) },
      }))
    ),
    fallback: 'blocking',
  };
};
